import React from 'react';
import { tokens } from '../tokens';

// Professional Button component replacing scattered button styles
const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  className = '',
  title,
  style: styleProp,
  ...props
}, ref) => {
  // Base styles using design tokens
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    fontWeight: tokens.typography.fontWeight.medium,
    borderRadius: tokens.borderRadius.lg,
    transition: `all ${tokens.transitionDuration.fast} ${tokens.transitionTimingFunction.ease}`,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    outline: 'none',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
    flexDirection: 'row',
  };

  // Size variants - Better vertical proportions
  const sizes = {
    sm: {
      padding: '8px 14px',
      fontSize: '13px',
      lineHeight: '1.3',
      gap: '6px',
    },
    md: {
      padding: '10px 18px',
      fontSize: '14px',
      lineHeight: '1.4',
      gap: '8px',
    },
    lg: {
      padding: '12px 24px',
      fontSize: '16px',
      lineHeight: '1.5',
      gap: '10px',
    },
  };

  // Cottagecore palette
  const C = {
    orange: '#EA7C1F',
    orangeDark: '#C25F0E',
    moss: '#6B7A3E',
    capRed: '#C14A33',
    card: 'var(--color-card, #FFFBEC)',
    surface: 'var(--color-surface, #EEE2BF)',
    border: 'var(--color-border, #D7C9A1)',
    text: 'var(--color-text, #2E2109)',
    muted: 'var(--color-text-muted, #6E5A38)',
    faint: 'var(--color-text-faint, #A39068)',
  };

  // Variant styles - Note: React inline styles don't support pseudo-selectors
  const variants = {
    primary: {
      backgroundColor: (disabled || loading) ? C.faint : C.orange,
      color: '#FFFFFF',
      borderColor: 'transparent',
      boxShadow: (disabled || loading) ? 'none' : '0 1px 0 rgba(46,33,9,0.1), 0 2px 8px rgba(234,124,31,0.25)',
    },
    secondary: {
      backgroundColor: (disabled || loading) ? C.surface : C.card,
      color: (disabled || loading) ? C.faint : C.text,
      borderColor: C.border,
      borderStyle: 'solid',
    },
    success: {
      backgroundColor: (disabled || loading) ? C.faint : C.moss,
      color: '#FFFFFF',
      borderColor: 'transparent',
    },
    danger: {
      backgroundColor: (disabled || loading) ? C.faint : C.capRed,
      color: '#FFFFFF',
      borderColor: 'transparent',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: (disabled || loading) ? C.faint : C.muted,
      borderColor: C.border,
      borderStyle: 'dashed',
    },
    outline: {
      backgroundColor: 'transparent',
      color: (disabled || loading) ? C.faint : C.orange,
      borderColor: (disabled || loading) ? C.border : C.orange,
      borderStyle: 'solid',
    },
  };

  // Combine all styles
  const buttonStyles = {
    ...baseStyles,
    ...sizes[size],
    ...variants[variant],
  };

  // For icon-only buttons (no children), add aria-label for accessibility
  const isIconOnly = Icon && !children && !loading;
  const ariaLabel = isIconOnly ? (title || props['aria-label'] || 'Button') : props['aria-label'];

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        strokeDasharray="32"
        strokeDashoffset="32"
        opacity="0.3"
      />
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        strokeDasharray="32"
        strokeDashoffset="24"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <button
      ref={ref}
      type="button"
      style={{ ...buttonStyles, ...styleProp }}
      disabled={disabled || loading}
      className={className}
      title={title}
      aria-label={ariaLabel}
      {...props}
    >
      {loading && (
        <>
          <LoadingSpinner />
          <span>{children}</span>
        </>
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <>
          <Icon style={{
            width: size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px',
            height: size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px',
            flexShrink: 0,
            marginRight: children ? (size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px') : 0
          }} />
          {children && <span>{children}</span>}
        </>
      )}
      {!loading && !Icon && children}
      {!loading && Icon && iconPosition === 'right' && (
        <>
          {children && <span>{children}</span>}
          <Icon style={{
            width: size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px',
            height: size === 'sm' ? '14px' : size === 'lg' ? '18px' : '16px',
            flexShrink: 0,
            marginLeft: children ? (size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px') : 0
          }} />
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
